// Rule hits and evidence rows of the compatibility result page.
import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import type { ProductCompatRuleHit } from '../api/endpoints';

const nice = (s: string) => s.replace(/_/g, ' ');
type EvidenceSide = {
  id?: number;
  name?: string;
  percent?: number;
  why?: Record<string, string | number | boolean>;
};

type EvidencePair = {
  left?: EvidenceSide;
  right?: EvidenceSide;
  score: number;
};
export function WhyChips({ why, side }: { why: Record<string, string | number | boolean>; side: 'left' | 'right' }) {
  const entries = Object.entries(why);
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-1)', marginTop: 'var(--s-1)' }}>
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="pill pill--compact"
          title={`${side}: ${k}=${String(v)}`}
        >
          {nice(k)}{v === true ? '' : `=${String(v)}`}
        </span>
      ))}
    </div>
  );
}

export function EvidencePairRow({ pair, idx }: { pair: EvidencePair; idx: number }) {
  const left = pair.left;
  const right = pair.right;

  return (
    <div
      key={idx}
      className="card card--bordered card--flat card--compact"
      style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s-2)' }}
    >
      <div className="compat-evidence-side compat-evidence-side--a" data-testid="compat-evidence-a">
        <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-1)' }}>
          <span className="compare-result-side__marker" aria-hidden />A
        </div>
        <div className="t-small" style={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {left?.id ? (
            <Link
              to={`/ingredients/${left.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--c-deep-teal)', transition: `color var(--t-fast) var(--ease)` }}
            >
              {left.name ?? '—'}
            </Link>
          ) : (left?.name ?? '—')}
          {left?.percent ? (
            <span className="t-mute"> · {left.percent}%</span>
          ) : null}
        </div>
        {left?.why && <WhyChips why={left.why} side="left" />}
      </div>

      <span className="t-small t-mute" style={{ flexShrink: 0, paddingTop: 'var(--s-1)' }}>×</span>

      <div className="compat-evidence-side compat-evidence-side--b" data-testid="compat-evidence-b">
        <div className="t-label compare-result-side__label" style={{ marginBottom: 'var(--s-1)' }}>
          <span className="compare-result-side__marker" aria-hidden />B
        </div>
        <div className="t-small" style={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {right?.id ? (
            <Link
              to={`/ingredients/${right.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--c-deep-teal)', transition: `color var(--t-fast) var(--ease)` }}
            >
              {right.name ?? '—'}
            </Link>
          ) : (right?.name ?? '—')}
          {right?.percent ? (
            <span className="t-mute"> · {right.percent}%</span>
          ) : null}
        </div>
        {right?.why && <WhyChips why={right.why} side="right" />}
      </div>

      <span className="t-small t-mute" style={{ flexShrink: 0, paddingTop: 'var(--s-1)' }}>
        {Math.round(pair.score)}%
      </span>
    </div>
  );
}

export function RuleHit({ hit }: { hit: ProductCompatRuleHit }) {
  const [open, setOpen] = useState(false);

  const cardMod  = hit.kind === 'synergy' ? 'card--synergy' : hit.kind === 'conflict' ? 'card--conflict' : '';
  const chipMod  = hit.kind === 'synergy' ? 'pill--teal'    : hit.kind === 'conflict' ? 'pill--coral'    : '';

  const evidencePairs: EvidencePair[] = (hit.evidence_pairs ?? []).map((pair) => ({
    left: pair.left,
    right: pair.right,
    score: pair.score,
  }));

  return (
    <div className={`card card--bordered card--flat ${cardMod}`} style={{ padding: 'var(--s-3)', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--s-2)' }}>
        <div style={{ minWidth: 0 }}>
          <div className="row-3" style={{ flexWrap: 'wrap', gap: 'var(--s-1)' }}>
            <span className={`pill pill--compact ${chipMod}`}>{hit.kind}</span>
            <span className="pill pill--compact">Rule {hit.rule_id}</span>
          </div>
          <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', marginTop: 'var(--s-1)', wordBreak: 'break-word' }}>
            {hit.summary || hit.name}
          </div>
          <div className="t-small t-mute" style={{ marginTop: 'var(--s-1)' }}>
            Evidence: {Math.round(hit.avg_evidence)}% · Δ {typeof hit.final_delta === 'number' ? hit.final_delta.toFixed(1) : '—'}
          </div>
        </div>

        <button
          type="button"
          onClick={() => { setOpen((o) => !o); }}
          className="btn btn--ghost btn--sm"
          style={{ flexShrink: 0 }}
          aria-expanded={open}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="stack-3"
            style={{ marginTop: 'var(--s-3)' }}
          >
            {hit.note && (
              <p className="t-small t-mute" style={{ margin: 0, wordBreak: 'break-word' }}>{hit.note}</p>
            )}

            {hit.details && Object.keys(hit.details).length > 0 && (
              <div className="g-2">
                {Object.entries(hit.details).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-3)' }}>
                    <span className="t-small t-mute" style={{ textTransform: 'capitalize' }}>{nice(k)}</span>
                    <span className="t-small" style={{ fontWeight: 500, wordBreak: 'break-all' }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            {evidencePairs.length > 0 && (
              <div className="card card--sand card--flat" style={{ padding: 'var(--s-3)' }}>
                <div className="t-label" style={{ color: 'var(--ink)', marginBottom: 'var(--s-2)' }}>
                  Evidence pairs
                </div>
                <div className="stack-2">
                  {evidencePairs.map((pair, idx) => (
                    <EvidencePairRow key={idx} pair={pair} idx={idx} />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{
        width: 18, height: 18,
        color: 'var(--ink-muted)',
        flexShrink: 0,
        transition: `transform var(--t-fast) var(--ease)`,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <polyline
        points="6 9 12 15 18 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
